/**
 * BuildScopedRecordInput
 *
 * Assembles the CreateRecordInput for an instant scoped Record create. Maps each
 * scope anchor onto the RecordType's OBJECT_REFERENCE field whose target object
 * type matches; aggregates multiple anchors of the same type into one field
 * value. Workflow steps default their assignees to the current user.
 *
 * Shared by the Library Files-tab "New record" menu (scope-driven) and the
 * sidebar quick-create Record step (user-picked object records).
 */

import type {
  CreateRecordInput,
  RecordData,
  RecordType,
} from '@/matic/features/records/types';

/** Minimal scope anchor: an object record of a given object type. */
export type ScopeRef = {typeId: string; recordId: string};

export type BuiltRecordInput = {
  input: CreateRecordInput;
  /** Scope anchors with no matching reference field on this type. */
  unresolved: ScopeRef[];
};

export function buildScopedRecordInput(
  recordType: RecordType,
  scopeItems: ScopeRef[],
  userId: string | undefined
): BuiltRecordInput {
  const idsByField = new Map<string, string[]>();
  const isMultiByField = new Map<string, boolean>();
  const unresolved: ScopeRef[] = [];

  for (const scope of scopeItems) {
    const field = recordType.fields.find(
      (f) =>
        f.fieldType === 'object_reference' &&
        f.config?.referenceObjectTypeId === scope.typeId
    );
    if (!field) {
      unresolved.push(scope);
      continue;
    }

    const ids = idsByField.get(field.name) ?? [];
    ids.push(scope.recordId);
    idsByField.set(field.name, ids);
    // Object-reference fields default to multi unless explicitly single.
    isMultiByField.set(field.name, field.config?.multi !== false);
  }

  const initialData: RecordData = {};
  for (const [fieldName, ids] of idsByField) {
    initialData[fieldName] = isMultiByField.get(fieldName) ? ids : ids[0];
  }

  const stepAssignees = recordType.workflowSteps.map((step) => ({
    stepOrder: step.stepOrder,
    assigneeIds: userId ? [userId] : [],
    groupIds: [],
  }));

  return {
    input: {
      recordTypeId: recordType.id,
      name: `New ${recordType.name}`,
      stepAssignees,
      initialData,
    },
    unresolved,
  };
}
